import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Settings, Trash2, Shield, Lock, Unlock, KeyRound, Copy, CheckCircle, AlertTriangle, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../contexts/LanguageContext';

const ALL_ROLES = [
  'SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER',
  'ACCOUNTANT', 'PROCUREMENT_OFFICER', 'HR_MANAGER',
  'STOREKEEPER', 'SECURITY_OFFICER',
  'CS_SUPERVISOR', 'ENGINEER', 'TECHNICIAN', 'TENANT'
];

// Phase 36 — friendly display labels for the role dropdown so the operational
// distinction (Engineer / Supervisor vs Technician) is clear to end users.
const ROLE_LABELS = {
  SUPERADMIN: 'Super Admin (App Owner)',
  FULL_ACCESS: 'Company Admin',
  ADMIN: 'Admin',
  CEO: 'CEO',
  MANAGER: 'Manager',
  ACCOUNTANT: 'Accountant',
  PROCUREMENT_OFFICER: 'Procurement Officer',
  HR_MANAGER: 'HR Manager',
  STOREKEEPER: 'Storekeeper',
  SECURITY_OFFICER: 'Security Officer',
  CS_SUPERVISOR: 'Customer Service Supervisor',
  ENGINEER: 'Engineer / Supervisor',
  TECHNICIAN: 'Technician',
  TENANT: 'Tenant',
};

const STAFF_ROLES = ['MANAGER', 'ACCOUNTANT', 'PROCUREMENT_OFFICER', 'HR_MANAGER', 'STOREKEEPER', 'SECURITY_OFFICER', 'CS_SUPERVISOR', 'ENGINEER', 'TECHNICIAN', 'TENANT'];

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'];

function TempPasswordDialog({ open, onClose, payload }) {
  const [copied, setCopied] = useState(false);
  if (!payload) return null;
  const { user, temp_password } = payload;
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <CheckCircle className="w-5 h-5" />Account Created
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-900">
              <strong>Save this temporary password now.</strong> It will not be shown again. Share it with the user — they must change it on first login.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">User</Label>
            <div className="text-sm font-medium text-slate-900">{user?.name} <span className="text-muted-foreground">({user?.role.replace('_', ' ')})</span></div>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Email</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 rounded bg-slate-100 text-slate-800 text-sm font-mono">{user?.email}</code>
              <Button data-testid="copy-email-btn" size="sm" variant="outline" onClick={() => copy(user.email)}><Copy className="w-3 h-3" /></Button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Temporary password</Label>
            <div className="flex items-center gap-2">
              <code data-testid="temp-password-value" className="flex-1 px-3 py-2 rounded bg-teal-50 border border-teal-200 text-teal-900 text-base font-mono font-bold tracking-wide">{temp_password}</code>
              <Button data-testid="copy-pw-btn" size="sm" variant="outline" onClick={() => copy(temp_password)}><Copy className="w-3 h-3" /></Button>
            </div>
          </div>
          <Button data-testid="copy-both-btn" onClick={() => copy(`Email: ${user.email}\nTemporary password: ${temp_password}\nLogin at: ${window.location.origin}/login`)} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">
            {copied ? 'Copied!' : 'Copy login details'}
          </Button>
        </div>
        <DialogFooter>
          <Button data-testid="temp-pw-close" onClick={onClose} variant="outline" className="w-full">I've saved it — close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UserManagement() {
  const { user } = useContext(AuthContext);
  const t = useT();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'TECHNICIAN', phone: '' });
  const [tempPwPayload, setTempPwPayload] = useState(null);

  // Determine which roles can be created based on current user
  const isSuperadmin = user?.role === 'SUPERADMIN';
  const creatableRoles = isSuperadmin ? ALL_ROLES : STAFF_ROLES;

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(r.data);
    } catch { toast.error('Failed to load users'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      const r = await axios.post(`${API}/users`, newUser, { withCredentials: true });
      toast.success('User created — temporary password generated');
      setShowNew(false);
      setNewUser({ name: '', email: '', role: 'TECHNICIAN', phone: '' });
      setTempPwPayload(r.data);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateRole = async (id, role) => {
    try {
      await axios.put(`${API}/users/${id}`, { role }, { withCredentials: true });
      toast.success('Role updated');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleBlock = async (id, blocked) => {
    const path = blocked ? 'unblock' : 'block';
    if (!blocked && !window.confirm('Block this user? They will be signed out and unable to log in.')) return;
    try {
      await axios.post(`${API}/users/${id}/${path}`, {}, { withCredentials: true });
      toast.success(blocked ? 'User unblocked' : 'User blocked');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleResetPassword = async (id) => {
    if (!window.confirm('Generate a new temporary password? The user will be signed out and forced to change it on next login.')) return;
    try {
      const r = await axios.post(`${API}/users/${id}/reset-password`, {}, { withCredentials: true });
      const target = users.find(u => u.id === id);
      setTempPwPayload({ user: target, temp_password: r.data.temp_password });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try { await axios.delete(`${API}/users/${id}`, { withCredentials: true }); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  // Per-building permissions dialog
  const [buildingsDlg, setBuildingsDlg] = useState({ open: false, user: null });
  const [availBuildings, setAvailBuildings] = useState([]);
  const [selectedBuildings, setSelectedBuildings] = useState(null); // null = all

  const openBuildings = async (u) => {
    try {
      const r = await axios.get(`${API}/buildings`, { withCredentials: true });
      setAvailBuildings(r.data || []);
      setSelectedBuildings(u.allowed_building_ids === undefined ? null : u.allowed_building_ids);
      setBuildingsDlg({ open: true, user: u });
    } catch (e) { toast.error('Failed to load buildings'); }
  };

  const toggleBuilding = (bid) => {
    setSelectedBuildings(prev => {
      const base = prev === null ? availBuildings.map(b => b.id) : [...prev];
      const i = base.indexOf(bid);
      if (i >= 0) base.splice(i, 1); else base.push(bid);
      return base;
    });
  };

  const saveBuildings = async () => {
    try {
      await axios.put(`${API}/users/${buildingsDlg.user.id}/buildings`, {
        allowed_building_ids: selectedBuildings
      }, { withCredentials: true });
      toast.success('Building access updated');
      setBuildingsDlg({ open: false, user: null });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <Layout>
      <div data-testid="users-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('users.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('users.subtitle')}</p>
          </div>
          {MANAGE.includes(user?.role) && (
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild>
                <Button data-testid="new-user-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-900"><Plus className="w-4 h-4 mr-1" />Create User</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create staff account</DialogTitle>
                  <p className="text-xs text-muted-foreground mt-1">A one-time temporary password will be auto-generated. Share it with the user — they'll change it on first login.</p>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Full Name</Label><Input data-testid="user-name-input" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="e.g. Ahmed Al-Mansoori" /></div>
                  <div><Label>Email</Label><Input type="email" data-testid="user-email-input" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="user@company.com" /></div>
                  <div><Label>Phone (optional)</Label><Input value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} placeholder="+971 50 ..." /></div>
                  <div><Label>Role</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {creatableRoles.map((r) => (
                          <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r.replace('_', ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-500 mt-1">
                      {isSuperadmin
                        ? 'As SUPERADMIN you can create User Admins (FULL_ACCESS) for any company.'
                        : 'You can create staff for your own company only. Only SUPERADMIN can create User Admins.'}
                    </p>
                  </div>
                  <Button data-testid="submit-user-btn" onClick={handleCreate} disabled={!newUser.name || !newUser.email} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">
                    Create &amp; Generate Temp Password
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs text-slate-600 uppercase tracking-wide">
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3 hidden md:table-cell">Email</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className={`hover:bg-slate-50 ${u.is_blocked ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-white font-bold text-xs">{u.name?.charAt(0)}</div>
                          )}
                          <div>
                            <div className="font-medium text-slate-900 text-sm flex items-center gap-1.5">
                              {u.name}
                              {u.must_change_password && (
                                <span title="Must change password on next login" className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">TEMP</span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground md:hidden">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">{u.email}</td>
                      <td className="px-4 py-3">
                        {MANAGE.includes(user?.role) && u.id !== user?.id && !u.is_blocked ? (
                          <Select value={u.role} onValueChange={(v) => handleUpdateRole(u.id, v)}>
                            <SelectTrigger data-testid={`role-select-${u.id}`} className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ALL_ROLES.filter(r => r !== 'SUPERADMIN' || isSuperadmin).map((r) => (
                                <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r.replace('_', ' ')}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
                            <Shield className="w-3 h-3" />{ROLE_LABELS[u.role] || u.role.replace('_', ' ')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {u.is_blocked ? (
                          <span data-testid={`status-blocked-${u.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                            <Lock className="w-3 h-3" />Blocked
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                            <CheckCircle className="w-3 h-3" />Active
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {MANAGE.includes(user?.role) && u.id !== user?.id && (
                          <div className="flex justify-end gap-1">
                            <Button
                              data-testid={`buildings-${u.id}`}
                              onClick={() => openBuildings(u)}
                              size="sm" variant="ghost" className="text-slate-600 hover:text-amber-600"
                              title="Set building access"
                            >
                              <Building2 className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`reset-pw-${u.id}`}
                              onClick={() => handleResetPassword(u.id)}
                              size="sm" variant="ghost" className="text-slate-600 hover:text-amber-600"
                              title="Reset password"
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`toggle-block-${u.id}`}
                              onClick={() => handleBlock(u.id, u.is_blocked)}
                              size="sm" variant="ghost"
                              className={u.is_blocked ? 'text-emerald-600' : 'text-amber-600'}
                              title={u.is_blocked ? 'Unblock user' : 'Block user'}
                            >
                              {u.is_blocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                            </Button>
                            <Button data-testid={`delete-user-${u.id}`} onClick={() => handleDelete(u.id)} size="sm" variant="ghost" className="text-red-500" title="Delete user">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.length === 0 && (
              <div className="p-10 text-center">
                <Settings className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No users yet</p>
              </div>
            )}
          </Card>
        )}

        <TempPasswordDialog
          open={!!tempPwPayload}
          onClose={() => setTempPwPayload(null)}
          payload={tempPwPayload}
        />

        {/* Per-building permissions dialog */}
        <Dialog open={buildingsDlg.open} onOpenChange={(v) => !v && setBuildingsDlg({ open: false, user: null })}>
          <DialogContent data-testid="buildings-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-amber-600" />Building access for {buildingsDlg.user?.name}</DialogTitle>
              <p className="text-xs text-muted-foreground mt-1">Restrict this user to specific buildings. Leave <strong>"All buildings"</strong> on to grant access to every building in the company.</p>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <label className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="buildings-all-toggle"
                  checked={selectedBuildings === null}
                  onChange={(e) => setSelectedBuildings(e.target.checked ? null : [])}
                  className="w-4 h-4 accent-amber-500"
                />
                <div>
                  <div className="font-semibold text-amber-900">All buildings</div>
                  <div className="text-xs text-amber-800">No restriction — user sees every building in the company</div>
                </div>
              </label>

              {selectedBuildings !== null && (
                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
                  {availBuildings.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground text-center">No buildings registered yet.</div>
                  ) : availBuildings.map(b => (
                    <label key={b.id} className="flex items-center gap-3 p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        data-testid={`bld-toggle-${b.id}`}
                        checked={(selectedBuildings || []).includes(b.id)}
                        onChange={() => toggleBuilding(b.id)}
                        className="w-4 h-4 accent-amber-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-slate-900 text-sm">{b.name}</div>
                        <div className="text-xs text-muted-foreground">{b.address || '—'}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {selectedBuildings !== null && (selectedBuildings || []).length === 0 && (
                <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                  ⚠ No buildings selected — this user will not see ANY buildings, assets, complaints, or PPM tasks.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBuildingsDlg({ open: false, user: null })}>Cancel</Button>
              <Button data-testid="save-buildings-btn" onClick={saveBuildings} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                Save access
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
